import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { authService, toFirebaseCompatibleUser } from '@/lib/firebase-auth-service';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { carPlay } from '@/services/capacitor';

// App Group token sync plugin for iMessage extension
interface AppGroupTokenPlugin {
  syncToken(options: { token: string; userId?: string; displayName?: string; email?: string; photoUrl?: string }): Promise<{ success: boolean }>;
  clearToken(): Promise<{ success: boolean }>;
}
const AppGroupToken = registerPlugin<AppGroupTokenPlugin>('AppGroupToken');

interface UserData {
  email: string;
  password: string;
  dateOfBirth: string;
  marketingEmailConsent: boolean;
  marketingConsentDate: string | null;
  termsAcceptedDate: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData?: Partial<UserData>) => Promise<void>;
  signInWithGoogle: () => Promise<any | null>;
  signInWithApple: () => Promise<any | null>;
  signOut: () => Promise<void>;
  getAuthToken: (forceRefresh?: boolean) => Promise<string | null>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  verifyPasswordResetCode: (oobCode: string) => Promise<string>;
  setPersistence: (mode: 'local' | 'session' | 'none') => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // Guard: prevent onAuthStateChanged from overwriting user with null right after sign-in
  const justSignedInRef = React.useRef(false);

  // Debug logging
  const debugLog = (message: string) => {
    console.log(`[Firebase Auth] ${message}`);
    if ((window as any).iosDebugLog) {
      (window as any).iosDebugLog(`[Firebase Auth] ${message}`);
    }
  };

  // Helper to pass auth token to CarPlay when user authenticates
  const syncAuthToCarPlay = async (user: User | null) => {
    if (!user || !Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return;
    }

    try {
      const token = await user.getIdToken();
      if (token) {
        await carPlay.setAuthToken(token);
        console.log('🚗 [CarPlay] Auth token synced to CarPlay');

        // Tell CarPlay whether Apple Music is connected in YapZap.
        // iOS system-level MusicKit auth is separate — we need to check the YapZap token.
        try {
          const res = await fetch('/api/music/apple-music/auth-status', {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          await carPlay.setMusicServiceConfig({ appleMusicConnected: !!data.connected });
        } catch {
          await carPlay.setMusicServiceConfig({ appleMusicConnected: false });
        }
      }
    } catch (error) {
      console.warn('🚗 [CarPlay] Failed to sync auth token:', error);
    }
  };

  // Helper to sync auth token to App Groups for iMessage extension
  const syncTokenToAppGroup = async (user: User | null) => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return;
    }

    if (!user) {
      try {
        await AppGroupToken.clearToken();
        console.log('📱 [AppGroup] Cleared token from App Group');
      } catch (error) {
        console.warn('📱 [AppGroup] Failed to clear token:', error);
      }
      return;
    }

    try {
      const token = await user.getIdToken(true); // force refresh for fresh token
      if (token) {
        await AppGroupToken.syncToken({
          token,
          userId: user.uid,
          displayName: user.displayName || undefined,
          email: user.email || undefined,
          photoUrl: user.photoURL || undefined,
        });
        console.log('📱 [AppGroup] Token synced for iMessage extension');
      }
    } catch (error) {
      console.warn('📱 [AppGroup] Failed to sync token:', error);
    }
  };

  // Log immediately when provider is created
  console.log('[Firebase Auth] AuthProvider component initialized');
  if ((window as any).iosDebugLog) {
    (window as any).iosDebugLog('[Firebase Auth] AuthProvider component initialized');
  }

  // Periodically refresh App Group token (every 45 min) so iMessage extension always has a valid token
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
    if (!user) return;

    const refreshInterval = setInterval(async () => {
      try {
        const token = await user.getIdToken(true);
        if (token) {
          await AppGroupToken.syncToken({
            token,
            userId: user.uid,
            displayName: user.displayName || undefined,
            email: user.email || undefined,
            photoUrl: user.photoURL || undefined,
          });
          console.log('📱 [AppGroup] Periodic token refresh synced');
        }
      } catch (error) {
        console.warn('📱 [AppGroup] Periodic token refresh failed:', error);
      }
    }, 45 * 60 * 1000); // 45 minutes

    return () => clearInterval(refreshInterval);
  }, [user]);

  useEffect(() => {
    let isCancelled = false;
    let timeoutId: NodeJS.Timeout;
    let forceCheckInterval: NodeJS.Timeout;
    let carPlayListenerCleanup: (() => void) | null = null;

    const setupAuth = async () => {
      try {
        // CRITICAL: Check if user has explicitly signed out BEFORE doing ANYTHING
        // This prevents the native Firebase module from being imported and auto-restoring the user
        console.log('🔍 [Firebase Auth] === AUTH SETUP STARTING ===');
        console.log('🔍 [Firebase Auth] Current URL:', window.location.href);
        console.log('🔍 [Firebase Auth] Checking for signed-out flag...');

        const signedOutFlag = localStorage.getItem('yapzap_signed_out');
        console.log('🔍 [Firebase Auth] Signed-out flag value:', signedOutFlag || 'NOT SET');

        // NOTE: We no longer return early here. The signed-out flag is checked in onAuthStateChanged
        // to block auto-restore of sessions, but we still set up the listener so NEW sign-ups work.
        if (signedOutFlag) {
          console.log('🚪 [Firebase Auth] Signed-out flag detected - will block auto-restore but allow new sign-ups');
        }

        console.log('✅ [Firebase Auth] Proceeding with auth setup (listener will check flag for auto-restore)');

        // Handle redirect results first
        const handleRedirect = async () => {
          try {
            const result = await authService.handleRedirectResult();
            if (result && !isCancelled) {
              debugLog('Redirect result received');
            }
          } catch (error: any) {
            console.error('❌ Google sign-in redirect error:', error.code, error.message);
            if (error.code === 'auth/unauthorized-domain') {
              console.error('🚫 Domain not authorized in Firebase Console');
              console.error('📝 Add this domain to Firebase: Authentication > Settings > Authorized domains');
            }
          }
        };

        await handleRedirect();

        if (isCancelled) return;

        debugLog('Setting up auth state listener');

        // Set up auth state listener
        const unsubscribe = await authService.onAuthStateChanged((user) => {
          if (isCancelled) return;

          // CRITICAL: Check if user intentionally signed out
          const signedOutFlag = localStorage.getItem('yapzap_signed_out');
          const isOnLoginPage = window.location.pathname === '/login';

          if (signedOutFlag) {
            console.log('🚪 [Auth State Listener] User signed out - ignoring auth state change');
            if (user) {
              console.log('🚪 [Auth State Listener] Blocking auto-restore of user:', user.email);
            }
            setUser(null);
            setLoading(false);
            return;
          }

          debugLog(`Auth state changed: ${user ? `User: ${user.email}` : 'No user'}`);
          if (user) {
            debugLog(`User details: ID=${user.uid}, Email=${user.email}`);
            console.log('🔥 [Firebase Auth] Setting user object:', user);
            justSignedInRef.current = false; // Clear guard — real user confirmed
            syncAuthToCarPlay(user);
            syncTokenToAppGroup(user);
          } else {
            // Don't overwrite user with null if we just signed in — the listener
            // may fire null before persistence loads the user on native
            if (justSignedInRef.current) {
              console.log('🔥 [Firebase Auth] Ignoring null — sign-in just happened');
              setLoading(false);
              return;
            }
            console.log('🔥 [Firebase Auth] No user - setting null');
          }
          setUser(user);
          setLoading(false);
          debugLog('Loading set to false - auth state determined');

          // Clear timeout since we got a response
          if (timeoutId) {
            clearTimeout(timeoutId);
          }

          // Clear force check if we found a user
          if (user && forceCheckInterval) {
            clearInterval(forceCheckInterval);
            debugLog('Clearing force check interval - user found');
          }
        });
        
        // FORCE CHECK: For Capacitor apps, actively poll for authentication
        // This handles cases where the auth state listener doesn't fire properly
        // BUT: Do NOT restore user if they've explicitly signed out
        let forceCheckCount = 0;
        forceCheckInterval = setInterval(async () => {
          if (isCancelled) return;
          forceCheckCount++;

          try {
            debugLog(`Force auth check #${forceCheckCount}`);

            // If user is null, check if this is an intentional sign-out
            // by checking if we're on the login page or if sign-out flag is set
            const isOnLoginPage = window.location.pathname === '/login';
            const signedOutFlag = localStorage.getItem('yapzap_signed_out');

            if (isOnLoginPage || signedOutFlag) {
              debugLog('Skipping force check - user intentionally signed out or on login page');
              clearInterval(forceCheckInterval);
              setLoading(false);
              return;
            }

            const currentUser = await authService.getCurrentUser();
            if (currentUser && !user) {
              debugLog(`Force check found authenticated user: ${currentUser.email}`);
              setUser(currentUser);
              setLoading(false);
              clearInterval(forceCheckInterval);
            } else if (!currentUser && forceCheckCount >= 6) {
              // After 6 attempts (30 seconds), stop checking
              debugLog('Force check: No user found after 30 seconds, stopping');
              clearInterval(forceCheckInterval);
              if (!user) {
                setLoading(false);
              }
            }
          } catch (error) {
            debugLog(`Force check error: ${error}`);
            if (forceCheckCount >= 6) {
              clearInterval(forceCheckInterval);
              if (!user) {
                setLoading(false);
              }
            }
          }
        }, 5000); // Check every 5 seconds
        
        // Set a fallback timeout to unblock loading if Firebase never responds
        timeoutId = setTimeout(() => {
          if (!isCancelled) {
            debugLog('Auth state listener timeout - forcing loading to false');
            setLoading(false);
            if (forceCheckInterval) {
              clearInterval(forceCheckInterval);
            }
          }
        }, 5000); // 5 second timeout — app must be usable quickly

        // Listen for CarPlay connection events - when CarPlay connects, send the auth token
        // This handles the case where the user is already logged in when CarPlay connects
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
          console.log('🚗 [CarPlay] Setting up carplayConnected listener...');
          carPlay.addListener('carplayConnected', async () => {
            console.log('🚗 [CarPlay] CarPlay connected event received!');
            // Get the current user and send their token
            try {
              const currentUser = await authService.getCurrentUser();
              if (currentUser) {
                console.log('🚗 [CarPlay] User is logged in, syncing token to CarPlay...');
                await syncAuthToCarPlay(currentUser);
                await syncTokenToAppGroup(currentUser);
              } else {
                console.log('🚗 [CarPlay] No user logged in when CarPlay connected');
              }
            } catch (error) {
              console.warn('🚗 [CarPlay] Error syncing token on CarPlay connect:', error);
            }
          }).then(listener => {
            carPlayListenerCleanup = () => listener.remove();
            console.log('🚗 [CarPlay] carplayConnected listener registered');
          }).catch(error => {
            console.warn('🚗 [CarPlay] Failed to add carplayConnected listener:', error);
          });

          // Listen for "Open in [Service]" taps from CarPlay.
          // '_system' routes through iOS URL handler → opens the native music app,
          // which then takes over the CarPlay display automatically.
          carPlay.addListener('openInMusicService', (data?: any) => {
            if (!data?.url) return;
            console.log('🚗 [CarPlay] Opening music service URL:', data.url);
            window.open(data.url, '_system');
          }).then(listener => {
            console.log('🚗 [CarPlay] openInMusicService listener registered');
          }).catch(error => {
            console.warn('🚗 [CarPlay] Failed to add openInMusicService listener:', error);
          });
        }

        return () => {
          if (unsubscribe) unsubscribe();
          if (forceCheckInterval) clearInterval(forceCheckInterval);
        };
      } catch (error) {
        debugLog(`Auth setup failed: ${error}`);
        console.error('Auth setup failed:', error);
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    setupAuth();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (forceCheckInterval) {
        clearInterval(forceCheckInterval);
      }
      if (carPlayListenerCleanup) {
        carPlayListenerCleanup();
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    // Clear the signed-out flag BEFORE auth attempt so onAuthStateChanged listener accepts the user
    localStorage.removeItem('yapzap_signed_out');
    justSignedInRef.current = true;
    // Auto-clear the guard after 10s — by then onAuthStateChanged should have the real state
    setTimeout(() => { justSignedInRef.current = false; }, 10000);
    const result = await authService.signInWithEmail(email, password);

    // Email auth always uses web Firebase SDK (even on native), so result is a UserCredential.
    const userCredential = result as any;
    if (userCredential?.user) {
      console.log('[signIn] Email sign-in successful:', userCredential.user.email);

      setUser(userCredential.user);
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      if (Capacitor.isNativePlatform()) {
        syncAuthToCarPlay(userCredential.user).catch(() => {});
        syncTokenToAppGroup(userCredential.user).catch(() => {});
      }
    }
  };

  const signUp = async (email: string, password: string, userData?: Partial<UserData>) => {
    // Clear the signed-out flag BEFORE auth attempt so onAuthStateChanged listener accepts the user
    localStorage.removeItem('yapzap_signed_out');
    // Set flag to indicate a new signup is in progress - used by Router to show loading screen
    sessionStorage.setItem('yapzap_just_signed_up', 'true');
    justSignedInRef.current = true; // Guard against null overwrites from onAuthStateChanged
    const result = await authService.signUpWithEmail(email, password, userData);

    // Email auth always uses web Firebase SDK (even on native), so result is a UserCredential.
    const userCredential = result as any;
    if (userCredential?.user) {
      console.log('[signUp] Email sign-up successful:', userCredential.user.email);

      setUser(userCredential.user);
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      if (Capacitor.isNativePlatform()) {
        syncAuthToCarPlay(userCredential.user).catch(() => {});
        syncTokenToAppGroup(userCredential.user).catch(() => {});
      }
    }
  };

  const signInWithGoogle = async () => {
    localStorage.removeItem('yapzap_signed_out');
    justSignedInRef.current = true;
    setTimeout(() => { justSignedInRef.current = false; }, 10000);
    sessionStorage.setItem('yapzap_just_signed_up', 'true');
    const result = await authService.signInWithGoogle();

    if (result) {
      // Result can be: native user object (has .uid directly), web UserCredential (has .user), or null
      const user = (result as any)?.user || result;
      const resolvedUser = user?.uid ? user : null;

      if (resolvedUser) {
        // If it's a native user, convert it; if it's a web User, use directly
        const finalUser = resolvedUser.getIdToken ? resolvedUser : toFirebaseCompatibleUser(resolvedUser);
        if (finalUser) {
          console.log('[signInWithGoogle] Setting user:', finalUser.email);
          setUser(finalUser);
          setLoading(false);
          queryClient.invalidateQueries({ queryKey: ["/api/user"] });
          if (Capacitor.isNativePlatform()) {
            syncAuthToCarPlay(finalUser).catch(() => {});
            syncTokenToAppGroup(finalUser).catch(() => {});
          }
        }
      }
    }

    return result;
  };

  const signInWithApple = async () => {
    localStorage.removeItem('yapzap_signed_out');
    justSignedInRef.current = true;
    setTimeout(() => { justSignedInRef.current = false; }, 10000);
    sessionStorage.setItem('yapzap_just_signed_up', 'true');
    const result = await authService.signInWithApple();

    if (result) {
      const user = (result as any)?.user || result;
      const resolvedUser = user?.uid ? user : null;

      if (resolvedUser) {
        const finalUser = resolvedUser.getIdToken ? resolvedUser : toFirebaseCompatibleUser(resolvedUser);
        if (finalUser) {
          console.log('[signInWithApple] Setting user:', finalUser.email);
          setUser(finalUser);
          setLoading(false);
          queryClient.invalidateQueries({ queryKey: ["/api/user"] });
          if (Capacitor.isNativePlatform()) {
            syncAuthToCarPlay(finalUser).catch(() => {});
            syncTokenToAppGroup(finalUser).catch(() => {});
          }
        }
      }
    }

    return result;
  };

  const signOut = async () => {
    try {
      console.log('🚪 [SignOut] Starting sign-out...');

      // 1. Set signed-out flag FIRST — prevents auth listener from restoring user
      localStorage.setItem('yapzap_signed_out', Date.now().toString());

      // 2. Immediately clear React state
      setUser(null);

      // 3. Firebase signOut — clears auth tokens from persistence (IndexedDB/memory)
      await authService.signOut();
      console.log('🚪 [SignOut] Firebase signOut completed');

      // 4. Clear token from App Groups (iMessage extension)
      await syncTokenToAppGroup(null).catch(() => {});

      // 5. Clear React Query cache
      queryClient.clear();

      // 6. iOS native: set flag for native SDK keychain cleanup
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        try {
          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.set({ key: 'yapzap_force_signout', value: 'true' });
        } catch { /* best-effort */ }
      }

      // 7. Clear app storage (NOT IndexedDB — Firebase signOut already handled that,
      //    and deleting IndexedDB while Firebase has open connections gets blocked)
      const keysToKeep = ['yapzap_signed_out'];
      Object.keys(localStorage).forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();

      // 8. Clear cookies for this origin (Firebase may store __session or similar)
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });

      console.log('🚪 [SignOut] All cleanup complete, navigating to login');
      window.location.href = '/login';
    } catch (error) {
      console.error('🚪 [SignOut] Error:', error);
      // Even on error, navigate to login — user wanted to sign out
      window.location.href = '/login';
    }
  };

  const getAuthToken = async (forceRefresh = false) => {
    return await authService.getIdToken(forceRefresh);
  };

  const sendPasswordResetEmail = async (email: string) => {
    return await authService.sendPasswordResetEmail(email);
  };

  const confirmPasswordReset = async (oobCode: string, newPassword: string) => {
    return await authService.confirmPasswordReset(oobCode, newPassword);
  };

  const verifyPasswordResetCode = async (oobCode: string) => {
    return await authService.verifyPasswordResetCode(oobCode);
  };

  const setPersistence = async (mode: 'local' | 'session' | 'none') => {
    return await authService.setPersistence(mode);
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    signOut,
    getAuthToken,
    sendPasswordResetEmail,
    confirmPasswordReset,
    verifyPasswordResetCode,
    setPersistence,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During lazy loading or initial render, the context might not be available yet
    // Return a safe default state instead of throwing to prevent crashes
    // This can happen during React Suspense boundary resolution
    console.warn('[Firebase Auth] Context not available - returning loading state');
    return {
      user: null,
      loading: true,
      signIn: async () => { throw new Error('Auth not initialized'); },
      signUp: async () => { throw new Error('Auth not initialized'); },
      signInWithGoogle: async () => { throw new Error('Auth not initialized'); },
      signInWithApple: async () => { throw new Error('Auth not initialized'); },
      signOut: async () => { throw new Error('Auth not initialized'); },
      getAuthToken: async () => null,
      sendPasswordResetEmail: async () => { throw new Error('Auth not initialized'); },
      confirmPasswordReset: async () => { throw new Error('Auth not initialized'); },
      verifyPasswordResetCode: async () => { throw new Error('Auth not initialized'); },
      setPersistence: async () => { throw new Error('Auth not initialized'); },
    } as AuthContextType;
  }
  return context;
}
